<template>
  <div class="service-visit-page">
    <KTCard ref="preview" v-bind:title="'Edit'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-service-visits' }">{{
            $t("MENU.ITEM.SERVICE.VISITS")
          }}</router-link>
          <i class="mr-2"></i>
          <small>{{ $t("LABELS.EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="service-visit-page__actions">
          <BackButton />
          <DeleteButton
            v-if="canMutateVisit"
            v-permission="['service-visits-delete']"
            @onDelete="onDelete"
          />
          <SaveButton
            v-if="canMutateVisit"
            :isLoading="isLoadingServiceVisit"
            @save="onSave"
          />
        </div>
      </template>
      <template v-slot:body>
        <b-form>
          <ServiceVisitsForm
            v-if="!isPageLoading"
            ref="ServiceVisitsForm"
            :form="form"
            :visitId="id"
            :readonly="isFinished"
          />
          <v-skeleton-loader
            v-if="isPageLoading"
            :loading="isPageLoading"
            type="article"
          />
          <v-overlay :value="!isPageLoading && isLoadingServiceVisit">
            <v-progress-circular indeterminate size="64" />
          </v-overlay>
        </b-form>
      </template>
      <template v-slot:foot>
        <div v-show="_.size(errors) > 0" role="alert" class="alert alert-danger">
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error[0] }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="service-visit-page__actions">
          <BackButton />
          <DeleteButton
            v-if="canMutateVisit"
            v-permission="['service-visits-delete']"
            @onDelete="onDelete"
          />
          <SaveButton
            v-if="canMutateVisit"
            :isLoading="isLoadingServiceVisit"
            @save="onSave"
          />
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  DELETE_SERVICE_VISIT,
  FETCH_SERVICE_VISIT,
  UPDATE_SERVICE_VISIT
} from "@/modules/service-visits/store/service-visits.module";
import { mapGetters } from "vuex";
import KTCard from "@/view/content/Card.vue";
import SaveButton from "@/view/content/forms/components/SaveButton.vue";
import {
  isServiceVisitFinished,
  serviceVisitFormPayload,
  serviceVisitToForm
} from "../services/service-visit.mapper";
import ServiceVisitsForm from "../components/ServiceVisitsForm.vue";

export default {
  name: "ServiceVisitsEdit",
  components: {
    KTCard,
    SaveButton,
    ServiceVisitsForm
  },
  data() {
    return {
      id: null,
      errors: {},
      item: null,
      isPageLoading: true,
      form: {
        machineId: null,
        statusId: null,
        priorityId: null,
        isOnRoad: false,
        onRoadNote: null,
        serviceLocationId: null,
        dateFrom: null,
        dateTo: null,
        duration: null,
        serviceMileage: null,
        hoursWorked: null,
        workDone: null,
        mechanicIds: [],
        serviceTypeIds: [],
        serviceTypeNotes: {},
        serviceTypeStatusIds: {},
        machineServiceSignalIds: [],
        machineServiceSignalNotes: {},
        machineServiceSignalStatusIds: {},
        customTasks: []
      }
    };
  },
  computed: {
    ...mapGetters(["isLoadingServiceVisit"]),
    isFinished() {
      return isServiceVisitFinished(this.item);
    },
    canMutateVisit() {
      return !this.isPageLoading && !this.isFinished;
    }
  },
  mounted() {
    this.id = this.$route.params.id;
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.VISITS"),
        route: { name: "list-service-visits" }
      },
      { title: this.$i18n.t("MENU.ITEM.EDIT") }
    ]);
    this.getServiceVisit();
  },
  methods: {
    getServiceVisit() {
      this.$store
        .dispatch(FETCH_SERVICE_VISIT, this.id)
        .then(data => {
          this.item = data.data;
          this.form = serviceVisitToForm(data.data);
          this.isPageLoading = false;
        })
        .catch(response => {
          if (response.status === 404) {
            this.$notify({
              group: "notify",
              type: "error",
              text: "Not Found"
            });
            this.$router.push({ name: "list-service-visits" });
          }
        });
    },
    onSave() {
      if (!this.canMutateVisit) {
        return;
      }

      const formComponent = this.$refs.ServiceVisitsForm;
      formComponent.$v.form.$touch();
      if (formComponent.$v.form.$anyError) {
        this.$notify({
          group: "notify",
          type: "error",
          title: this.$t("ALERTS.ERROR"),
          text: "Please fill all required fields correctly"
        });
        return;
      }

      this.$store
        .dispatch(UPDATE_SERVICE_VISIT, { id: this.id, payload: this.payload() })
        .then(data => {
          this.errors = data.data.errors;
          this.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          this.$router.push({ name: "list-service-visits" });
        })
        .catch(response => {
          this.errors = response.data.errors;
          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: response.data.message
          });
        });
    },
    onDelete() {
      this.$dialog.confirm(this.$t("BUTTON.PLEASE_CONFIRM")).then(() => {
        this.$store.dispatch(DELETE_SERVICE_VISIT, this.id).then(data => {
          this.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          this.$router.push({ name: "list-service-visits" });
        });
      });
    },
    payload() {
      return serviceVisitFormPayload(
        this.form,
        true,
        this.$refs.ServiceVisitsForm.defaultItemStatusId()
      );
    }
  }
};
</script>
